"use client";

import React from "react";
import { isRenderableChatMessage } from "@/lib/agent/renderableChatMessage";
import {
  AgentActionPayload,
  AgentWidget,
  AgentMessage as AgentMessageType,
  AgentThinkingTraceStep,
  PresetOption,
} from "@/types/createAgent";
import { getWidgetPresetOptions } from "@/lib/agent/widgetPresets";
import { resolveDesignerChoice } from "@/lib/designer/actionProjection";
import { isCommitPresetAction } from "@/lib/agent/presetPresentation";
import type { DesignerRenderActionId } from "@/lib/agent/designerActionPresentation";
import type { DesktopRenderActionState } from "../desktopRenderAction";
import AgentMessage from "../message/AgentMessage";
import ThinkingTrace from "../ThinkingTrace";
import AgentTypingDots from "../AgentTypingDots";

function normalizePresetLabel(value: string): string {
  return value.trim().toLowerCase();
}

// Stable fallbacks: fresh [] identities on every render would retrigger
// downstream effects keyed on these props (e.g. AgentPresets panel reset).
const EMPTY_WIDGETS: AgentWidget[] = [];
const EMPTY_PRESETS: PresetOption[] = [];

interface ChatMessageListProps {
  messages: AgentMessageType[];
  isTyping: boolean;
  thinkingStatus: string | null;
  thinkingTrace: AgentThinkingTraceStep[];
  presetsLoading: boolean;
  loading: boolean;
  actionsDisabled?: boolean;
  isModifying: boolean;
  exampleTypingInProgress: boolean;
  visiblePresets: PresetOption[];
  authLoading: boolean;
  processingPresetId: string | null;
  selectedPresetLabelsByMessageId: Record<string, string>;
  desktopFooterActionId?: DesignerRenderActionId | null;
  desktopFooterAuthorityPending?: boolean;
  onDesktopRenderActionStateChange?: (state: DesktopRenderActionState) => void;
  freeFrontPreview?: boolean;
  /** When provided, clicking an image in a message opens this modal (the one next to Start Over) instead of the message's local modal */
  onOpenFullSizeImage?: (initialView: "front" | "back") => void;
  onPresetClick: (
    preset: PresetOption,
    source?: AgentActionPayload["source"]
  ) => void;
  onPresetSelect: (
    messageId: string,
    preset: PresetOption,
    source?: AgentActionPayload["source"]
  ) => void;
  onPresetGenerate: (
    messageId: string,
    preset: PresetOption,
    source?: AgentActionPayload["source"]
  ) => void;
  onPresetChatMore: (
    messageId: string,
    preset: PresetOption | null,
    source?: AgentActionPayload["source"]
  ) => void;
  onComposerModifyRequest?: () => void;
  onComposerModifyExit?: () => void;
}

/**
 * Renders the visible conversation in turn order. While a reply is pending,
 * its thinking trace follows the latest user turn; once the reply exists, the
 * trace sits immediately before that assistant message.
 */
export default function ChatMessageList({
  messages,
  isTyping,
  thinkingStatus,
  thinkingTrace,
  presetsLoading,
  loading,
  actionsDisabled = false,
  isModifying,
  exampleTypingInProgress,
  visiblePresets,
  authLoading,
  processingPresetId,
  selectedPresetLabelsByMessageId,
  desktopFooterActionId,
  desktopFooterAuthorityPending = false,
  onDesktopRenderActionStateChange,
  freeFrontPreview = false,
  onOpenFullSizeImage,
  onPresetClick,
  onPresetSelect,
  onPresetGenerate,
  onPresetChatMore,
  onComposerModifyRequest,
  onComposerModifyExit,
}: ChatMessageListProps) {
  const visibleMessages = messages.filter(isRenderableChatMessage);
  const latestAssistantMessageId = [...visibleMessages]
    .reverse()
    .find(message => message.role === "assistant")?.id;
  const latestConversationMessage = messages[messages.length - 1];
  // Streaming appends an empty assistant placeholder after the user turn. It
  // is intentionally not renderable yet, so the active trace belongs at the
  // end of the visible transcript until that same reply gains content.
  const thinkingTraceAssistantId =
    latestConversationMessage?.role === "assistant" &&
    isRenderableChatMessage(latestConversationMessage)
      ? latestConversationMessage.id
      : undefined;
  const getSelectedPresetLabel = (
    messageId: string,
    messagePresets: PresetOption[]
  ): string | null => {
    if (messagePresets.length === 0) {
      return null;
    }

    // Search the FULL list (hidden turns included): preset clicks send their
    // label as a hidden user turn — no bubble renders, but it still supplies
    // the compact receipt for that completed decision.
    const fullIndex = messages.findIndex(message => message.id === messageId);
    const nextUserMessage =
      fullIndex >= 0
        ? messages.slice(fullIndex + 1).find(message => message.role === "user")
        : undefined;

    if (!nextUserMessage?.content) {
      return null;
    }

    // A one-button commit surface is no longer current once the customer has
    // moved on to another turn. Its stored action content can be the terse
    // payload label ("Generate") while the renderer expands that same action
    // to "Generate preview", so exact text matching alone leaves the old card
    // behind as a dim, disabled duplicate. A single commit is unambiguous; if
    // the customer typed instead of clicking it, the newer turn superseded it
    // all the same.
    if (
      messagePresets.length === 1 &&
      isCommitPresetAction(messagePresets[0].actionId)
    ) {
      return messagePresets[0].label;
    }

    const selectedPreset = messagePresets.find(
      preset =>
        normalizePresetLabel(preset.label) ===
        normalizePresetLabel(nextUserMessage.content)
    );

    return selectedPreset?.label ?? null;
  };

  return (
    <>
      {visibleMessages.map((message, messageIndex) => {
        const messageWidgets = message.widgets ?? EMPTY_WIDGETS;
        const widgetPresets = getWidgetPresetOptions(messageWidgets);
        // An explicit per-message action surface is authoritative, including
        // an empty one. Save-point restoration may also hydrate a synthesized
        // global fallback; it must not reappear under a composer-only question
        // whose assistant message was persisted with `presets: []`.
        const hasAuthoredActionSurface =
          message.presets !== undefined || message.widgets !== undefined;
        const messagePresets = hasAuthoredActionSurface
          ? (message.presets ?? EMPTY_PRESETS)
          : message.id === latestAssistantMessageId &&
              !presetsLoading &&
              !exampleTypingInProgress
            ? (visiblePresets as PresetOption[])
            : EMPTY_PRESETS;
        const presetsActive =
          message.id === latestAssistantMessageId &&
          (messagePresets.length > 0 || messageWidgets.length > 0) &&
          !loading &&
          !isModifying &&
          !isTyping &&
          !presetsLoading;
        const storedSelectedPresetLabel =
          selectedPresetLabelsByMessageId[message.id];
        // Widgets carry the pick across reloads (selection.data.selectedLabel
        // round-trips through persisted history); the in-memory map wins for
        // the live session, the hidden-turn fallback covers legacy sessions.
        const widgetSelectedLabel = messageWidgets
          .map(widget => widget.selection?.data?.selectedLabel)
          .find((value): value is string => typeof value === "string");
        const selectedPresetLabel =
          storedSelectedPresetLabel ??
          widgetSelectedLabel ??
          (presetsActive
            ? null
            : getSelectedPresetLabel(
                message.id,
                messagePresets.length > 0 ? messagePresets : widgetPresets
              ));
        const getMessageActionSource = (
          preset: PresetOption | null,
          source?: AgentActionPayload["source"]
        ) =>
          preset
            ? resolveDesignerChoice(message.id, preset, messageWidgets, source)
                ?.source
            : { messageId: message.id };
        const hasCurrentThinkingTrace =
          message.id === thinkingTraceAssistantId && thinkingTrace.length > 0;
        const messageThinkingTrace = hasCurrentThinkingTrace
          ? thinkingTrace
          : (message.thinkingTrace ?? []);

        return (
          <React.Fragment key={message.id}>
            {messageThinkingTrace.length > 0 && (
              <ThinkingTrace
                steps={messageThinkingTrace}
                isActive={
                  hasCurrentThinkingTrace && (isTyping || presetsLoading)
                }
              />
            )}
            <AgentMessage
              message={message}
              onOpenFullSizeImage={onOpenFullSizeImage}
              presetOptions={messagePresets}
              widgetOptions={messageWidgets}
              onPresetClick={(preset, origin) => {
                const source = getMessageActionSource(preset, origin);
                if (source) onPresetClick(preset, source);
              }}
              onPresetSelect={(preset, origin) => {
                const source = getMessageActionSource(preset, origin);
                if (source) onPresetSelect(message.id, preset, source);
              }}
              onPresetGenerate={(preset, origin) => {
                const source = getMessageActionSource(preset, origin);
                if (source) onPresetGenerate(message.id, preset, source);
              }}
              onPresetChatMore={(preset, origin) => {
                const source = getMessageActionSource(preset, origin);
                if (source) onPresetChatMore(message.id, preset, source);
              }}
              onComposerModifyRequest={onComposerModifyRequest}
              onComposerModifyExit={onComposerModifyExit}
              presetsActive={presetsActive}
              presetsDisabled={
                actionsDisabled || !!processingPresetId || authLoading
              }
              presetsLoading={
                message.id === latestAssistantMessageId && presetsLoading
              }
              processingPresetId={processingPresetId}
              selectedPresetLabel={selectedPresetLabel}
              desktopFooterActionId={desktopFooterActionId}
              desktopFooterAuthorityPending={desktopFooterAuthorityPending}
              onDesktopRenderActionStateChange={
                message.id === latestAssistantMessageId
                  ? onDesktopRenderActionStateChange
                  : undefined
              }
              isLatest={messageIndex === visibleMessages.length - 1}
              freeFrontPreview={freeFrontPreview}
            />
          </React.Fragment>
        );
      })}
      {!thinkingTraceAssistantId &&
        thinkingTrace.length > 0 &&
        (isTyping || presetsLoading) && (
          <ThinkingTrace steps={thinkingTrace} isActive />
        )}
      {(isTyping || presetsLoading) && thinkingTrace.length === 0 && (
        <div className="flex justify-start">
          <AgentTypingDots
            status={
              thinkingStatus ||
              (presetsLoading ? "Preparing next options" : null)
            }
            isFinalizing={presetsLoading}
          />
        </div>
      )}
    </>
  );
}
